import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Select } from "@repo/o37-ui";
import AdminActivityModal from '../components/AdminActivityModal';
import PermissionEditor from '../components/PermissionEditor';
import { trackAdminAction } from '../hooks/useAdminTracker';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type VDRPermission = {
  room_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
  vdr_rooms?: { name: string };
};

type DomainGroupPermission = {
  group_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
  domain_groups?: { name: string };
};

type FundraisingPermission = {
  fundraise_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
  fundraises?: { name: string };
};

type HoldingsCompanyPermission = {
  company_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
  holdings_companies?: { name: string };
};

type User = {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
  page_permissions?: { page_key: string }[];
  vdr_permissions?: VDRPermission[];
  domain_group_permissions?: DomainGroupPermission[];
  fundraising_permissions?: FundraisingPermission[];
  holdings_company_permissions?: HoldingsCompanyPermission[];
  mfa_required?: boolean;
  mfa_enabled_at?: string;
  is_suspended?: boolean;
  suspended_at?: string;
  suspended_reason?: string;
};

type AdminLog = {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  device_info: any;
  location_info: any;
  action_type: string;
  action_category: string | null;
  metadata: any;
  path: string | null;
  target_id: string | null;
  target_type: string | null;
  session_id: string | null;
  created_at: string;
};

type AggregatedUser = {
  id: string;
  email: string | null;
  name: string | null;
  lastActive: string;
  firstSeen: string;
  actionCount: number;
  location: string;
  country?: string;
  totalTime: number;
  devices: { platform?: string; browser?: string; screenSize?: string; timezone?: string; userAgent?: string }[];
  logs: AdminLog[];
};

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingLogsUser, setViewingLogsUser] = useState<AggregatedUser | null>(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setError('');
      const response = await fetch(`${API_URL}/users`, {
        headers: {
            'x-user-role': localStorage.getItem('app_role') || 'user'
        }
      });
      if (response.status === 403) {
          setError('Access Denied: You do not have permission to view users.');
          setUsers([]);
          return;
      }
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('Failed to load users');
      }
    } catch (e) {
      console.error(e);
      setError('Error connecting to server');
    }
  };

  const handleCreate = async (userData: any) => {
      try {
        const response = await fetch(`${API_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        if (response.ok) {
            const created = await response.json();
            await trackAdminAction('create_user', {
              target_email: userData.email,
              target_name: userData.name,
              role: userData.role
            }, { targetId: created?.user?.id, targetType: 'user' });
            
            setShowCreateModal(false);
            loadUsers();
        } else {
            const err = await response.json();
            alert(err.error);
        }
      } catch (e) {
          alert('Failed to create user');
      }
  };

  const handleUpdate = async (userId: string, updateData: any) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}/permissions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (response.ok) {
        await trackAdminAction('update_permissions', {
          target_email: editingUser?.email,
          changes: updateData
        }, { targetId: userId, targetType: 'user' });
        
        setEditingUser(null);
        loadUsers();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to update user');
      }
    } catch (e) {
      alert('Failed to update user');
    }
  };

  const handleViewLogs = async (user: User) => {
    try {
      setLoadingLogs(true);
      const response = await fetch(`${API_URL}/admin-logs/user/${user.id}`, {
        headers: { 'x-user-role': userRole }
      });

      if (!response.ok) {
        alert('Failed to load user logs');
        return;
      }

      const logs = await response.json() as AdminLog[];
      
      let totalTime = 0;
      let lastActive = user.created_at;
      let firstSeen = user.created_at;
      let location = 'Unknown';
      let country: string | undefined;
      const devices: AggregatedUser['devices'] = [];
      const seenDevices = new Set<string>();

      logs.forEach(log => {
        if (new Date(log.created_at) > new Date(lastActive)) {
          lastActive = log.created_at;
        }
        if (new Date(log.created_at) < new Date(firstSeen)) {
          firstSeen = log.created_at;
        }
        if (log.action_type === 'time_on_page') {
          const ms = Number(log.metadata?.duration_ms) || 0;
          totalTime += (ms / 1000);
        }
        if (location === 'Unknown' && (log.city || log.country)) {
          location = [log.city, log.country].filter(Boolean).join(', ');
        }
        if (!country && log.country) {
          country = log.country;
        }
        if (log.device_info) {
          const di = log.device_info;
          const deviceKey = `${di.platform || 'Unknown'}-${di.screen_width || 0}x${di.screen_height || 0}`;
          if (!seenDevices.has(deviceKey)) {
            seenDevices.add(deviceKey);
            devices.push({
              platform: di.platform,
              browser: parseBrowser(di.user_agent || log.user_agent),
              screenSize: di.screen_width && di.screen_height ? `${di.screen_width}x${di.screen_height}` : undefined,
              timezone: di.timezone,
              userAgent: di.user_agent || log.user_agent || undefined
            });
          }
        }
      });

      const aggregatedUser: AggregatedUser = {
        id: user.id,
        email: user.email,
        name: user.name,
        lastActive,
        firstSeen,
        actionCount: logs.length,
        location,
        country,
        totalTime,
        devices,
        logs
      };

      setViewingLogsUser(aggregatedUser);
    } catch (e) {
      console.error(e);
      alert('Error loading user logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const parseBrowser = (ua?: string | null): string => {
    if (!ua) return 'Unknown';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    return 'Other';
  };

  const getAccessSummary = (user: User) => {
    if (user.role === 'super_admin') return 'Full Access';
    const pages = user.page_permissions?.length || 0;
    const rooms = user.vdr_permissions?.length || 0;
    const groups = user.domain_group_permissions?.length || 0;
    const raises = user.fundraising_permissions?.length || 0;
    const companies = user.holdings_company_permissions?.length || 0;
    const parts = [];
    if (pages > 0) parts.push(`${pages} page${pages > 1 ? 's' : ''}`);
    if (rooms > 0) parts.push(`${rooms} room${rooms > 1 ? 's' : ''}`);
    if (groups > 0) parts.push(`${groups} group${groups > 1 ? 's' : ''}`);
    if (raises > 0) parts.push(`${raises} raise${raises > 1 ? 's' : ''}`);
    if (companies > 0) parts.push(`${companies} compan${companies > 1 ? 'ies' : 'y'}`);
    return parts.length > 0 ? parts.join(', ') : 'No access';
  };

  return (
    <div className="min-h-full">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Users</h1>
          <p className="text-neutral-500">Manage system access and permissions.</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} text="Add User" />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-4 py-3 font-medium text-neutral-600">Name</th>
                  <th className="px-4 py-3 font-medium text-neutral-600">Email</th>
                  <th className="px-4 py-3 font-medium text-neutral-600">Role</th>
                  <th className="px-4 py-3 font-medium text-neutral-600">Status</th>
                  <th className="px-4 py-3 font-medium text-neutral-600">MFA</th>
                  <th className="px-4 py-3 font-medium text-neutral-600">Access</th>
                  <th className="px-4 py-3 font-medium text-neutral-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                      {error ? 'No users to display due to error.' : 'No users found.'}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-neutral-50/50">
                    <td className="px-4 py-3 font-medium">{user.name || 'No Name'}</td>
                    <td className="px-4 py-3 text-neutral-500">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                        ${user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' : 'bg-neutral-100 text-neutral-700'}`}>
                        {user.role === 'super_admin' ? 'Super Admin' : 'User'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.is_suspended ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Suspended
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                        {user.mfa_enabled_at ? (
                            <span className="text-green-600 font-medium">Enabled</span>
                        ) : user.mfa_required ? (
                            <span className="text-amber-600">Required</span>
                        ) : (
                            <span className="text-neutral-400">Off</span>
                        )}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {getAccessSummary(user)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="outline"
                          className="h-7 text-xs px-2"
                          onClick={() => handleViewLogs(user)}
                          disabled={loadingLogs}
                          text="Logs"
                        />
                        <Button 
                          variant="secondary"
                          className="h-7 text-xs px-2"
                          onClick={() => setEditingUser(user)}
                          text="Edit"
                        />
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showCreateModal && (
        <CreateUserModal 
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
        />
      )}

      {editingUser && (
        <EditUserModal 
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onUpdate={handleUpdate}
        />
      )}

      {viewingLogsUser && (
        <AdminActivityModal 
          user={viewingLogsUser}
          logs={viewingLogsUser.logs}
          onClose={() => setViewingLogsUser(null)}
        />
      )}
    </div>
  );
}

// Hooks for fetching VDRs and Domain Groups
const useVdrRooms = () => {
    const [rooms, setRooms] = useState<any[]>([]);
    useEffect(() => {
        const role = localStorage.getItem('app_role') || 'user';
        const userId = localStorage.getItem('app_user_id') || '';
        fetch(`${API_URL}/vdr`, { headers: { 'x-user-role': role, 'x-user-id': userId }})
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setRooms(data);
            })
            .catch(console.error);
    }, []);
    return rooms;
};

const useDomainGroups = () => {
    const [groups, setGroups] = useState<any[]>([]);
    useEffect(() => {
        const role = localStorage.getItem('app_role') || 'user';
        const userId = localStorage.getItem('app_user_id') || '';
        fetch(`${API_URL}/domain-groups`, { headers: { 'x-user-role': role, 'x-user-id': userId }})
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setGroups(data);
            })
            .catch(console.error);
    }, []);
    return groups;
};

const useFundraises = () => {
    const [fundraises, setFundraises] = useState<any[]>([]);
    useEffect(() => {
        const role = localStorage.getItem('app_role') || 'user';
        const userId = localStorage.getItem('app_user_id') || '';
        fetch(`${API_URL}/fundraising/raises`, { headers: { 'x-user-role': role, 'x-user-id': userId }})
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setFundraises(data);
            })
            .catch(console.error);
    }, []);
    return fundraises;
};

const useHoldingsCompanies = () => {
    const [companies, setCompanies] = useState<any[]>([]);
    useEffect(() => {
        const role = localStorage.getItem('app_role') || 'user';
        const userId = localStorage.getItem('app_user_id') || '';
        fetch(`${API_URL}/holdings/companies`, { headers: { 'x-user-role': role, 'x-user-id': userId }})
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setCompanies(data);
            })
            .catch(console.error);
    }, []);
    return companies;
};

function EditUserModal({ user, onClose, onUpdate }: { user: User, onClose: () => void, onUpdate: (id: string, data: any) => void }) {
    const [role, setRole] = useState(user.role);
    const [pages, setPages] = useState<string[]>(user.page_permissions?.map(p => p.page_key) || []);

    const [vdrPermissions, setVdrPermissions] = useState<any[]>(
        user.vdr_permissions?.map(p => ({ roomId: p.room_id, accessLevel: p.access_level })) || []
    );

    const [domainGroupPermissions, setDomainGroupPermissions] = useState<any[]>(
        user.domain_group_permissions?.map(p => ({ groupId: p.group_id, accessLevel: p.access_level })) || []
    );

    const [fundraisingPermissions, setFundraisingPermissions] = useState<any[]>(
        user.fundraising_permissions?.map(p => ({ fundraiseId: p.fundraise_id, accessLevel: p.access_level })) || []
    );

    const [holdingsCompanyPermissions, setHoldingsCompanyPermissions] = useState<any[]>(
        user.holdings_company_permissions?.map(p => ({ companyId: p.company_id, accessLevel: p.access_level })) || []
    );

    const [mfaRequired, setMfaRequired] = useState(user.mfa_required || false);

    // Suspension state
    const [showSuspendDialog, setShowSuspendDialog] = useState(false);
    const [suspendReason, setSuspendReason] = useState('');

    const allRooms = useVdrRooms();
    const allDomainGroups = useDomainGroups();
    const allFundraises = useFundraises();
    const allHoldingsCompanies = useHoldingsCompanies();

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onUpdate(user.id, { role, pages, vdrPermissions, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions, mfaRequired });
    };

    const handleDisableMFA = () => {
        if (!confirm("Are you sure you want to disable MFA for this user? They will need to set it up again.")) return;
        onUpdate(user.id, { disableMFA: true });
    };

    const handleSuspend = () => {
        onUpdate(user.id, { suspend: true, suspendedReason: suspendReason });
        setShowSuspendDialog(false);
        setSuspendReason('');
    };

    const handleUnsuspend = () => {
        if (!confirm("Are you sure you want to unsuspend this user? They will be able to log in again.")) return;
        onUpdate(user.id, { suspend: false });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-100">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Edit User</h2>
                        <p className="text-sm text-neutral-500">{user.name} • {user.email}</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2 rounded-lg hover:bg-neutral-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {/* Role & MFA Section */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Role</label>
                                <Select value={role} onChange={e => setRole(e.target.value)} className="w-full">
                                    <option value="user">User</option>
                                    <option value="super_admin">Super Admin</option>
                                </Select>
                            </div>
                            
                            <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                                <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Security</label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={mfaRequired}
                                            onChange={e => setMfaRequired(e.target.checked)}
                                            className="h-4 w-4 rounded border-neutral-300"
                                        />
                                        <span>Require MFA</span>
                                    </label>

                                    {user.mfa_enabled_at && (
                                        <Button
                                            type="button"
                                            variant="danger-outline"
                                            className="h-7 text-xs w-full justify-center mt-2"
                                            onClick={handleDisableMFA}
                                            text="Force Disable MFA"
                                        />
                                    )}

                                    <div className="border-t border-neutral-200 pt-2 mt-2">
                                        {user.is_suspended ? (
                                            <div>
                                                <div className="text-xs text-red-600 mb-2">
                                                    Account is suspended
                                                    {user.suspended_reason && <span className="block text-neutral-500 mt-1">Reason: {user.suspended_reason}</span>}
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    className="h-7 text-xs w-full justify-center"
                                                    onClick={handleUnsuspend}
                                                    text="Unsuspend User"
                                                />
                                            </div>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="danger-outline"
                                                className="h-7 text-xs w-full justify-center"
                                                onClick={() => setShowSuspendDialog(true)}
                                                text="Suspend User"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Permissions Section */}
                        {role === 'user' && (
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-3">Permissions</label>
                                <PermissionEditor
                                    currentPermissions={pages}
                                    vdrPermissions={vdrPermissions}
                                    domainGroupPermissions={domainGroupPermissions}
                                    fundraisingPermissions={fundraisingPermissions}
                                    holdingsCompanyPermissions={holdingsCompanyPermissions}
                                    allRooms={allRooms}
                                    allDomainGroups={allDomainGroups}
                                    allFundraises={allFundraises}
                                    allHoldingsCompanies={allHoldingsCompanies}
                                    onChange={(p, v, dg, fp, hcp) => {
                                        setPages(p);
                                        setVdrPermissions(v);
                                        setDomainGroupPermissions(dg);
                                        setFundraisingPermissions(fp);
                                        setHoldingsCompanyPermissions(hcp);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </form>
                
                <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50 flex justify-end gap-3">
                    <Button variant="outline" type="button" onClick={onClose} text="Cancel" />
                    <Button type="submit" onClick={(e: any) => { e.preventDefault(); handleSubmit(e); }} text="Save Changes" />
                </div>
            </div>

            {/* Suspend User Dialog */}
            {showSuspendDialog && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-2">Suspend User</h3>
                        <p className="text-sm text-neutral-500 mb-4">
                            This will prevent <strong>{user.email}</strong> from logging in.
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Reason (optional)</label>
                            <textarea
                                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-200 focus:border-neutral-400 outline-none resize-none"
                                rows={3}
                                value={suspendReason}
                                onChange={e => setSuspendReason(e.target.value)}
                                placeholder="Enter reason for suspension..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={() => { setShowSuspendDialog(false); setSuspendReason(''); }} text="Cancel" />
                            <Button variant="danger" onClick={handleSuspend} text="Suspend User" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CreateUserModal({ onClose, onCreate }: { onClose: () => void, onCreate: (data: any) => void }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('user');

    const [pages, setPages] = useState<string[]>([]);
    const [vdrPermissions, setVdrPermissions] = useState<any[]>([]);
    const [domainGroupPermissions, setDomainGroupPermissions] = useState<any[]>([]);
    const [fundraisingPermissions, setFundraisingPermissions] = useState<any[]>([]);
    const [holdingsCompanyPermissions, setHoldingsCompanyPermissions] = useState<any[]>([]);
    const [mfaRequired, setMfaRequired] = useState(false);

    const allRooms = useVdrRooms();
    const allDomainGroups = useDomainGroups();
    const allFundraises = useFundraises();
    const allHoldingsCompanies = useHoldingsCompanies();

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onCreate({ name, email, password, role, pages, vdrPermissions, domainGroupPermissions, fundraisingPermissions, holdingsCompanyPermissions, mfaRequired });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-100">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Create User</h2>
                        <p className="text-sm text-neutral-500">Add a new user to the system</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2 rounded-lg hover:bg-neutral-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Name</label>
                                <input 
                                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-200 focus:border-neutral-400 outline-none" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)} 
                                    required 
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Email</label>
                                <input 
                                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-200 focus:border-neutral-400 outline-none" 
                                    type="email" 
                                    value={email} 
                                    onChange={e => setEmail(e.target.value)} 
                                    required 
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Password</label>
                                <input 
                                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-200 focus:border-neutral-400 outline-none" 
                                    type="password" 
                                    value={password} 
                                    onChange={e => setPassword(e.target.value)} 
                                    required 
                                    placeholder="••••••••"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1.5">Role</label>
                                <Select value={role} onChange={e => setRole(e.target.value)} className="w-full">
                                    <option value="user">User</option>
                                    <option value="super_admin">Super Admin</option>
                                </Select>
                            </div>
                        </div>

                        {/* MFA Toggle */}
                        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={mfaRequired} 
                                    onChange={e => setMfaRequired(e.target.checked)} 
                                    className="h-4 w-4 rounded border-neutral-300"
                                />
                                <span>Require MFA on first login</span>
                            </label>
                        </div>

                        {/* Permissions */}
                        {role === 'user' && (
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-3">Permissions</label>
                                <PermissionEditor
                                    currentPermissions={pages}
                                    vdrPermissions={vdrPermissions}
                                    domainGroupPermissions={domainGroupPermissions}
                                    fundraisingPermissions={fundraisingPermissions}
                                    holdingsCompanyPermissions={holdingsCompanyPermissions}
                                    allRooms={allRooms}
                                    allDomainGroups={allDomainGroups}
                                    allFundraises={allFundraises}
                                    allHoldingsCompanies={allHoldingsCompanies}
                                    onChange={(p, v, dg, fp, hcp) => {
                                        setPages(p);
                                        setVdrPermissions(v);
                                        setDomainGroupPermissions(dg);
                                        setFundraisingPermissions(fp);
                                        setHoldingsCompanyPermissions(hcp);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </form>

                <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50 flex justify-end gap-3">
                    <Button variant="outline" type="button" onClick={onClose} text="Cancel" />
                    <Button type="submit" onClick={(e: any) => { e.preventDefault(); handleSubmit(e); }} text="Create User" />
                </div>
            </div>
        </div>
    );
}
